import { Supertype, supertypeClass, property, remote, Remoteable, Persistable } from '@haventech/amorphic';

import { Principal } from './Principal';
import { SecurityContext } from './SecurityContext';

@supertypeClass
export class AuthenticatedAdminPrincipal extends Remoteable(Persistable(Supertype)) implements Principal {
  // These secure elements are NEVER transmitted

  @property({ toServer: true })
  email: string = '';

  @property({ toServer: false })
  newEmail: string = '';

  @property({ toServer: false })
  firstName: string = '';

  @property({ toServer: false })
  lastName: string = '';

  @property({ toServer: false })
  emailValidated: boolean = false;

  @property({ toServer: false })
  suspended: boolean = false;

  @property({ toServer: false })
  lockedOut: boolean = false;

  @property({ toServer: false, toClient: false, type: Date })
  unsuccesfulLogins: Array<Date> = [];

  @property({ toServer: false })
  passwordExpires: Date;

  @property({ toServer: false })
  mustChangePassword: boolean = false;

  @property({ toServer: false, toClient: false, type: String, sensitiveData: true })
  previousSalts: Array<string> = [];

  @property({ toServer: false, toClient: false, type: String })
  previousHashes: Array<string> = [];

  @property({
    toServer: false,
    values: {
      user: 'User', // A normal user
      defaultAdminRole: 'Administrator', // An administrative user})
    },
  })
  role: string = 'user';

  @property({ toServer: false, persist: false })
  securityContext: SecurityContext;

  // These are never received.  You can mess with your passwork assuming you are logged in but never see it

  @property({ toClient: false, toServer: false, sensitiveData: true })
  passwordHash: string;

  @property({ toClient: false, toServer: false, sensitiveData: true })
  passwordSalt: string;

  @property({ toClient: false, toServer: false, sensitiveData: true })
  passwordChangeHash: string = '';

  @property({ toClient: false, toServer: false, sensitiveData: true })
  passwordChangeSalt: string = '';

  @property({ toClient: false, toServer: false })
  passwordChangeExpires: Date;

  @property({ toClient: false, toServer: false })
  validateEmailCode: string;

  constructor() {
    super();
  }

  static new() {
    return new AuthenticatedAdminPrincipal().init();
  }

  init() {
    return this;
  }

  getController() {
    const amorphicContext: any = this.amorphic;
    return amorphicContext.controller;
  }

  @remote()
  changeEmail(email) {
    throw Error('changeEmail should not be called from Admin principal');
  }
  /**
   * Create a password hash and save the object
   *
   * @param password
   * @returns {*} promise (true) when done
   * throws an exception if the password does not meet password rules
   */

  establishPassword(password, expires, noValidate, forceChange) {
    throw Error('establishPassword should not be called from Admin principal');
  }

  /**
   * Check password rules for a new password
   *
   * @param password
   * @return {*}
   */
  validateNewPassword(password) {
    throw Error('validateNewPassword should not be called from Admin principal');
  }

  /**
   * Return a password hash
   *
   * @param password
   * @param salt
   * @return {*}
   */

  getHash(password: string, salt: string): Promise<string> {
    throw Error('getHash should not be called from Admin principal');
  }

  /**
   * Get a secure random string for the salt
   *
   * @return {*}
   */
  getSalt(): Promise<string> {
    throw Error('getSalt should not be called from Admin principal');
  }

  /*
   * Make registration pending verification of a code usually sent by email
   */
  setEmailVerificationCode() {
    throw Error('setEmailVerificationCode should not be called from Admin principal');
  }

  /*
   * Verify the email code passed in and reset the principal record to allow registration to proceed
   */
  consumeEmailVerificationCode(code) {
    throw Error('setEmailVerificationCode should not be called from Admin principal');
  }

  /**
   * Create a one-way hash for changing passwords
   * @returns {*}
   */
  async setPasswordChangeHash(): Promise<string> {
    throw Error('setPasswordChangeHash should not be called from Admin principal');
  }

  /**
   * Consume a password change token and change the password
   *
   * @param token
   * @returns {*}
   */
  consumePasswordChangeToken(token, newPassword) {
    throw Error('consumePasswordChangeToken should not be called from Admin principal');
  }

  /**
   * Verify a password on login (don't reveal password vs. user name is bad)
   *
   * @param password
   * @returns {*}
   */
  authenticate(password, loggedIn, novalidate) {
    throw Error('authenticate should not be called from Admin principal');
  }

  badLogin() {
    throw Error('badLogin should not be called from Admin principal');
  }
}
